import { z } from 'zod';

import { IMPORT_KINDS, IMPORT_STATUSES } from './constants.js';

export const importKindSchema = z.enum(IMPORT_KINDS);
export const importStatusSchema = z.enum(IMPORT_STATUSES);

/** Severity ladder used to classify problems in a parsed CSV row. */
export const IMPORT_ISSUE_SEVERITIES = ['error', 'warning'] as const;
export type ImportIssueSeverity = (typeof IMPORT_ISSUE_SEVERITIES)[number];

export const importIssue = z.object({
  /** 1-based row number within the CSV body (header row is row 1). */
  row: z.number().int().positive(),
  /** Column name as it appears in the header, when applicable. */
  column: z.string().optional(),
  severity: z.enum(IMPORT_ISSUE_SEVERITIES),
  /** Short, human-readable message. */
  message: z.string(),
  /** Stable code (e.g. `MISSING_COLUMN`, `INVALID_STATE`, `GEOCODER_NO_MATCH`). */
  code: z.string(),
});
export type ImportIssue = z.infer<typeof importIssue>;

export const importPreviewRow = z.object({
  row: z.number().int().positive(),
  name: z.string(),
  addressLine1: z.string(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  bin: z.string().nullable(),
  bbl: z.string().nullable(),
  borough: z.string().nullable(),
  geocoded: z.boolean(),
  unitsToCreate: z.number().int().nonnegative(),
  willSkip: z.boolean(),
});
export type ImportPreviewRow = z.infer<typeof importPreviewRow>;

export const importPreviewResult = z.object({
  totalRows: z.number().int().nonnegative(),
  validRows: z.number().int().nonnegative(),
  skippedRows: z.number().int().nonnegative(),
  errorRows: z.number().int().nonnegative(),
  warnings: z.array(importIssue),
  errors: z.array(importIssue),
  /** First N rows for the UI preview table. Capped server-side. */
  sample: z.array(importPreviewRow),
  /** Aggregate counts shown in the wizard. */
  totals: z.object({
    buildingsToCreate: z.number().int().nonnegative(),
    unitsToCreate: z.number().int().nonnegative(),
  }),
});
export type ImportPreviewResult = z.infer<typeof importPreviewResult>;

export const createImportInput = z.object({
  uploadId: z.string().uuid(),
  /** Optional friendly label shown in the imports list / activity feed. */
  label: z.string().trim().max(120).optional(),
});
export type CreateImportInput = z.infer<typeof createImportInput>;

export const commitImportInput = z.object({
  /** Soft confirmation that the user has reviewed warnings before commit. */
  ackWarnings: z.boolean().optional().default(false),
});
export type CommitImportInput = z.infer<typeof commitImportInput>;

/** Super-admin only: same shape as `createImportInput` plus the target org. */
export const seedBuildingsInput = createImportInput.extend({
  /** The org the buildings will be inserted under. Required even though
   *  the URL already names it — keeps tests and webhook clients explicit. */
  targetOrgId: z.string().uuid(),
});
export type SeedBuildingsInput = z.infer<typeof seedBuildingsInput>;

export const importJobSummary = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  targetOrgId: z.string().uuid().nullable(),
  kind: importKindSchema,
  status: importStatusSchema,
  label: z.string().nullable(),
  totalRows: z.number().int().nonnegative(),
  validRows: z.number().int().nonnegative(),
  skippedRows: z.number().int().nonnegative(),
  errorRows: z.number().int().nonnegative(),
  committedRows: z.number().int().nonnegative(),
  unitsCreated: z.number().int().nonnegative(),
  preview: importPreviewResult.nullable(),
  failureReason: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
});
export type ImportJobSummary = z.infer<typeof importJobSummary>;
